/**
 * MVP module — quality category scorer (Phase B milestone B1).
 * Policy anchors: MVP guide §2 (docs/reference/mvp/scoring-methodology-py2026.md),
 * Trad guide §2.8 selection mechanics, delta doc §1.1.
 *
 * Rules implemented here (each pinned by src/lib/mvp/__tests__):
 *  - pool = pathway qualityMeasureIds minus QCDR ids, ∩ counts-with-data
 *  - per-measure scoring via the EXISTING decile calculator (mips-scoring.ts) —
 *    topped-out/caps/floors come free from the benchmarks file
 *  - collection-type dedup (parameterized): one slot per measureId, higher wins
 *  - reserved slot (default 'submission-strategy', business-logic.mdc): highest-scoring
 *    OUTCOME or HIGH-PRIORITY measure, assuming lower-scoring outcomes are left out of the
 *    submission (detail.leaveOut lists them); 'cms-submitted' = best outcome first over
 *    everything with data (CMS worked examples). If none reported, the slot scores 0/10
 *    and the denominator is NOT reduced
 *  - remaining slots by points desc; ties broken by ASCENDING measure id
 *  - population health: higher of scoreable 479/484 → +points numerator, +10 denominator;
 *    neither scoreable → excluded entirely (label, no denominator effect)
 *  - small-practice bonus +6 NUMERATOR ONLY (never the denominator)
 *  - improvement: achievement percentages (bonus EXCLUDED both sides),
 *    ((cur − prior)/prior) × 10, floor 0, cap 10pp, added AFTER the division
 *  - category pct capped at 100
 */
import { scoreQualityMeasure } from '../mips-scoring';
import type { ParameterSet } from './parameters/py2026';
import type {
  ConfidenceFlag, EngineDeps, MeasureCounts, PopHealthEntry, PriorYearInputs, ReservedSlotRule,
} from './types';

/** QCDR measures carry alphanumeric ids (ACR10, IRIS39…); registry-calculable ids are numeric. */
export function isQcdrMeasureId(measureId: string): boolean {
  return !/^\d+$/.test(measureId);
}

export interface ScoredCandidate {
  measureId: string;
  submissionMethod: string;
  points: number;
  hasBenchmark: boolean;
  isOutcome: boolean;
  isHighPriority: boolean;
}

export interface MvpQualityArgs {
  counts: MeasureCounts[];             // pre-aggregated per measure+method
  qualityPool: string[];               // pathway's qualityMeasureIds
  popHealth: { m479: PopHealthEntry | null; m484: PopHealthEntry | null };
  isSmallPractice: boolean;
  priorYear: PriorYearInputs;
  /** One selection slot per measureId (higher-scoring collection type wins). */
  dedupCollectionTypes: boolean;
  params: ParameterSet;
  deps: EngineDeps;
  /**
   * Required-and-scoreable measure count override. Defaults to
   * params.mvpRequiredQualityMeasures (4). Used for: the Traditional row (6) and
   * the small-practice Part B claims denominator reduction (MVP guide §2.1 —
   * engine hook exists, no UI in v1; also how CMS Example 2's den=20 is fixtured).
   */
  requiredCount?: number;
  /** Population health enters MVP quality only — false for the Traditional row. */
  includePopHealth?: boolean;
  /**
   * When the small-practice Part B reduction applies, the outcome-slot rule is
   * inapplicable — ALL required slots fill by points (CMS Example 2 scores two
   * non-outcome Part B measures with denominator 20). Default false: with no
   * outcome/high-priority candidate, the slot is HELD OPEN at 0/10 and only
   * required−1 slots fill (Trad §2.8 item 3).
   */
  outcomeSlotWaived?: boolean;
  /** See EngineInputs.reservedSlotRule. Default 'submission-strategy'. */
  reservedSlotRule?: ReservedSlotRule;
  /**
   * What-if performance-rate overrides, keyed `${measureId}:${submissionMethod}`.
   * Overrides the RATE only — completeness/case-minimum still derive from counts.
   */
  prOverrides?: Record<string, number>;
}

export interface MvpQualityResult {
  pct: number;                         // 0-100, capped
  achievementPct: number;              // bonus-free basis (improvement inputs for NEXT year)
  flags: ConfidenceFlag[];
  detail: {
    poolSize: number;
    qcdrExcluded: string[];
    withData: number;
    selected: Array<{ measureId: string; submissionMethod: string; points: number; reservedSlot: boolean }>;
    dedupDropped: Array<{ measureId: string; submissionMethod: string; points: number }>;
    unfilledSlots: number;             // required slots with nothing reported (score 0 each)
    /** Scored outcome measures to leave OUT of the submission (a high-priority measure holds the slot). */
    leaveOut: Array<{ measureId: string; submissionMethod: string; points: number }>;
    popHealth: { scored: boolean; measureId: string | null; points: number | null; reason: string };
    smallPracticeBonus: number;
    improvementPP: number;
    numeratorPoints: number;
    denominatorPoints: number;
  };
}

export function scoreMvpQuality(args: MvpQualityArgs): MvpQualityResult {
  const { counts, qualityPool, popHealth, isSmallPractice, priorYear, dedupCollectionTypes, params, deps } = args;
  const includePopHealth = args.includePopHealth ?? true;
  const flags: ConfidenceFlag[] = [];

  // --- Pool ---
  const qcdrExcluded = qualityPool.filter(isQcdrMeasureId);
  const supportedPool = new Set(qualityPool.filter(id => !isQcdrMeasureId(id)));
  const candidateCounts = counts.filter(c => supportedPool.has(c.measureId));

  // --- Score each candidate via the existing decile calculator ---
  let scored: ScoredCandidate[] = candidateCounts.map(c => {
    const benchmark = deps.getBenchmark(c.measureId, c.submissionMethod);
    const meta = deps.getMeasureMeta(c.measureId);
    const prOverride = args.prOverrides?.[`${c.measureId}:${c.submissionMethod}`];
    const result = scoreQualityMeasure({
      denominator: c.den,
      performanceMet: c.met,
      performanceNotMet: c.not,
      numeratorExceptions: c.exc,
      practiceSize: isSmallPractice ? 'small' : 'large',
      benchmark,
      ...(prOverride != null ? { overridePerformanceRate: prOverride } : {}),
    });
    return {
      measureId: c.measureId,
      submissionMethod: c.submissionMethod,
      points: result.score,
      hasBenchmark: benchmark != null,
      isOutcome: meta?.isOutcome ?? false,
      isHighPriority: meta?.isHighPriority ?? false,
    };
  });

  // --- Collection-type dedup: one slot per measureId, higher-scoring instance wins ---
  const dedupDropped: MvpQualityResult['detail']['dedupDropped'] = [];
  if (dedupCollectionTypes) {
    const best = new Map<string, ScoredCandidate>();
    for (const s of scored) {
      const cur = best.get(s.measureId);
      if (!cur || s.points > cur.points) {
        if (cur) dedupDropped.push({ measureId: cur.measureId, submissionMethod: cur.submissionMethod, points: cur.points });
        best.set(s.measureId, s);
      } else {
        dedupDropped.push({ measureId: s.measureId, submissionMethod: s.submissionMethod, points: s.points });
      }
    }
    scored = [...best.values()];
  }

  // --- Selection: reserved outcome/high-priority slot, then top-(N-1) by points ---
  // Ties broken by ASCENDING measure id (Trad §2.8; numeric-aware compare).
  const byPointsThenId = (a: ScoredCandidate, b: ScoredCandidate) =>
    b.points - a.points ||
    a.measureId.localeCompare(b.measureId, undefined, { numeric: true, sensitivity: 'base' });

  const required = args.requiredCount ?? params.mvpRequiredQualityMeasures;
  const outcomes = scored.filter(s => s.isOutcome).sort(byPointsThenId);
  const highPriority = scored.filter(s => !s.isOutcome && s.isHighPriority).sort(byPointsThenId);
  const reserved = (args.reservedSlotRule ?? 'submission-strategy') === 'cms-submitted'
    ? outcomes[0] ?? highPriority[0] ?? null
    : [...outcomes, ...highPriority].sort(byPointsThenId)[0] ?? null;

  const rest = scored.filter(s => s !== reserved).sort(byPointsThenId);
  let selected: ScoredCandidate[];
  if (reserved) {
    selected = [reserved, ...rest.slice(0, required - 1)];
  } else if (args.outcomeSlotWaived) {
    // Part B reduction: no outcome slot to hold — all required slots fill by points.
    selected = [...scored].sort(byPointsThenId).slice(0, required);
  } else {
    // Outcome/high-priority slot HELD OPEN at 0/10 (Trad §2.8 item 3) — no denominator relief.
    selected = rest.slice(0, required - 1);
  }
  const unfilledSlots = required - selected.length; // incl. an empty reserved slot (0/10)
  // When no counted measure is an outcome, the high-priority measure holds the slot only if no outcome
  // measure is submitted (CMS reserves the best SUBMITTED outcome first) — every scored outcome stays out.
  const leaveOut = reserved && !selected.some(s => s.isOutcome)
    ? outcomes.map(s => ({ measureId: s.measureId, submissionMethod: s.submissionMethod, points: s.points }))
    : [];

  if (selected.some(s => !s.hasBenchmark)) flags.push('no-historical-benchmark');

  // --- Population health: higher of the scoreable pair (MVP pathways only) ---
  const ph = includePopHealth
    ? pickPopHealth(popHealth)
    : { points: null, detail: { scored: false, measureId: null, points: null, reason: 'Not applicable (Traditional pathway)' } as MvpQualityResult['detail']['popHealth'] };
  const phScored = ph.points != null;

  // --- Assembly (MVP guide §2.6) ---
  const measurePoints = selected.reduce((sum, s) => sum + s.points, 0);
  const numeratorNoBonus = measurePoints + (ph.points ?? 0);
  const denominatorPoints =
    required * params.pointsPerMeasureMax + (phScored ? params.popHealthDenominatorWhenScored : 0);

  const reportedAtLeastOne = selected.length > 0;
  const bonus = isSmallPractice && reportedAtLeastOne ? params.smallPracticeQualityBonus : 0;

  const achievementPct = (numeratorNoBonus / denominatorPoints) * 100;

  // --- Improvement (achievement %, bonus excluded both sides) ---
  let improvementPP = 0;
  if (priorYear.qualityAchievementPct != null && priorYear.qualityAchievementPct > 0 && priorYear.sameIdentifier) {
    improvementPP = ((achievementPct - priorYear.qualityAchievementPct) / priorYear.qualityAchievementPct)
      * params.qualityImprovementMultiplier;
    improvementPP = Math.max(0, Math.min(params.qualityImprovementCapPP, improvementPP));
    if (priorYear.reconstructed) flags.push('reconstructed-improvement');
  }

  const pct = Math.min(100, ((numeratorNoBonus + bonus) / denominatorPoints) * 100 + improvementPP);

  return {
    pct,
    achievementPct,
    flags,
    detail: {
      poolSize: qualityPool.length,
      qcdrExcluded,
      withData: candidateCounts.length,
      selected: selected.map(s => ({
        measureId: s.measureId,
        submissionMethod: s.submissionMethod,
        points: s.points,
        reservedSlot: s === reserved,
      })),
      dedupDropped,
      unfilledSlots,
      leaveOut,
      popHealth: ph.detail,
      smallPracticeBonus: bonus,
      improvementPP,
      numeratorPoints: numeratorNoBonus + bonus,
      denominatorPoints,
    },
  };
}

function pickPopHealth(popHealth: { m479: PopHealthEntry | null; m484: PopHealthEntry | null }): {
  points: number | null;
  detail: MvpQualityResult['detail']['popHealth'];
} {
  const scoreable: Array<{ id: string; points: number }> = [];
  if (popHealth.m479 && popHealth.m479.caseMinMet && popHealth.m479.points != null) {
    scoreable.push({ id: '479', points: popHealth.m479.points });
  }
  if (popHealth.m484 && popHealth.m484.caseMinMet && popHealth.m484.points != null) {
    scoreable.push({ id: '484', points: popHealth.m484.points });
  }
  if (scoreable.length === 0) {
    const reason = (popHealth.m479 || popHealth.m484)
      ? 'Neither population health measure scoreable — excluded from numerator and denominator'
      : 'No population health data available — excluded from numerator and denominator';
    return { points: null, detail: { scored: false, measureId: null, points: null, reason } };
  }
  // CMS assigns ONLY the higher of the two (MVP guide §2.4) — never both.
  const winner = scoreable.sort((a, b) => b.points - a.points)[0];
  return {
    points: winner.points,
    detail: { scored: true, measureId: winner.id, points: winner.points, reason: `Higher of the scoreable pair (${winner.id})` },
  };
}
